import get from 'lodash-es/get'
import keys from 'lodash-es/keys'

import { getConfigBySite } from '../../config/sites'
import { config } from '../config'
import { toTitle } from '../utils/formatString'
import {
  getUserCartItems,
  getUserCollections,
  getUserFavoriteCollectionID
} from './user'

import {
  IItem,
  IMatch,
  IPreviews,
  IProxySearchResultsDecorated
} from '../../typings/searchResults'
import { IRootState } from '../reducers'

import { hostedItemPriceCents } from '../constants/searchResultsOverrides'

/**
 * Check if an item is favourite
 * @param  {Object} state
 * @param  {Object} item
 * @return {Boolean}
 */
export const isInFavourites = (state: IRootState, item: IMatch) => {
  const collections = getUserCollections(state)
  const favoriteCollectionID = getUserFavoriteCollectionID(state)

  return (
    (collections[favoriteCollectionID] &&
      collections[favoriteCollectionID].bookmarks[item.id] &&
      collections[favoriteCollectionID].bookmarks[item.id].optimisticStatus !==
        'deleted') ||
    false
  )
}

/**
 * Check if an item is in bookmark
 * @param  {Object} state
 * @param  {Object} item
 * @return {Boolean}
 */
export const isInBookmarks = (state: IRootState, item: IMatch) => {
  const collections = getUserCollections(state)

  const nonFavoriteCollections = keys(collections)
    .map(collectionID => collections[collectionID])
    .filter(collection => collection.special_role !== 'favorite')

  return (
    nonFavoriteCollections.some(collection => collection.bookmarks[item.id]) ||
    false
  )
}

/**
 * Check if an item is in cart
 * @param  {Object} state
 * @param  {Object} item
 * @return {Boolean}
 */
export const isInCart = (state: IRootState, item: IMatch) => {
  const cartItems = getUserCartItems(state) as any[]
  return (
    cartItems.some((cartItemId: any) => cartItemId.item_id === item.id) || false
  )
}

const getFooterLink = (state: IRootState, item: IMatch) => {
  const { currentSite } = state.common
  const footerLink = getConfigBySite(currentSite, 'item_view.footer_link')
  return (
    footerLink && {
      href: footerLink.href.replace(
        /\{(.*)\}/g,
        (match: any, field: keyof IMatch) => {
          return item[field]
        }
      ),
      label: footerLink.label
    }
  )
}

const getItemSmallPreviewUrlPath = (item: IMatch): string => {
  const previewPaths = getConfigBySite(
    'defaults',
    'ui.item_small_preview_paths'
  ) as string[]
  const previewPath: string = previewPaths
    .map(path => get(item.previews, path))
    // return first non falsy value
    .reduce((prevPath, currPath) => {
      if (prevPath) {
        return prevPath
      }
      return currPath
    })
  if (previewPath) {
    return previewPath
  }
  return `${config.assets.PUBLIC_PATH}/${getConfigBySite(
    'defaults',
    'ui.default_user_image_path'
  )}`
}

const getPDItemImageAspectRatio = (item: IMatch) => {
  if (!item.image_urls || item.image_urls.length <= 0) {
    return null
  }

  const imgUrls = item.image_urls
  const firstImage = imgUrls[0]

  return firstImage.width / firstImage.height
}

const price = (state: IRootState, match: IMatch) => {
  // When 'Hosting & Setup' filter is selected we need to override search result
  // prices with a fixed $19/month price
  const priceCents = isHostingSetupFilterSelected(state)
    ? hostedItemPriceCents
    : match.price_cents
  return priceCents ? priceCents.toString().slice(0, -2) : ''
}

const pricePeriod = (state: IRootState, match: IMatch) => {
  return isHostingSetupFilterSelected(state) ? 'month' : null
}

const isHostingSetupFilterSelected = (state: IRootState) => {
  return state.searchPage.hostingSetupSearch
}

export const decorateSearchResult = (
  state: IRootState,
  match: IMatch
): IItem => {
  const newMatch = {
    ...match,
    ui: {
      aspectRatio: getPDItemImageAspectRatio(match),
      footerLink: getFooterLink(state, match),
      isInBookmarks: isInBookmarks(state, match),
      isInCart: isInCart(state, match),
      isInFavourites: isInFavourites(state, match),
      price: price(state, match),
      pricePeriod: pricePeriod(state, match),
      smallPreviewUrl: getItemSmallPreviewUrlPath(match)
    }
  }
  return newMatch
}

/**
 * Compute additional property for ease of use in the UI
 * It does not modify any existing props from the API
 * @param  {Object} global redux state
 * @return {Object} the formatted result selected from state
 */
export const searchResults = (
  state: IRootState
): IProxySearchResultsDecorated => {
  return {
    ...state.searchPage.results,
    matches: state.searchPage.results.matches.map((match: IMatch) =>
      decorateSearchResult(state, match)
    )
  }
}
